import React, { useState } from "react";

import * as Dialog from "@radix-ui/react-dialog";
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  Clock,
  Trash2,
  X,
} from "lucide-react";

import { FLOW } from "./flowCopy";
import UploadProgress, { groupChecks, passLabel } from "./UploadProgress";
import { DeleteButton, DeleteConfirm } from "./UploadRow";
import type { PlanCheck, PlanFile } from "../../Types/AccountPlan";
import {
  aheadText,
  roughRange,
  suggestLeaving,
} from "../../Utils/AccountPlans/queueText";

interface Props {
  open: boolean;
  /** On the plan page itself instead of a pop-up (Sushant, 2026-10-31: the pop-up
   * repeated the page). No overlay and no Close: the page is where it lives. */
  inline?: boolean;
  /** The version just uploaded, as the latest overview poll has it; null until the first poll. */
  file: PlanFile | null;
  /** Shown before the first poll brings the version back. */
  filename: string;
  /** Only the AE may withdraw or try again. */
  canEdit: boolean;
  busy?: boolean;
  error?: string | null;
  /** Close; the plan keeps going and the person is notified (spec NTF-05). */
  onClose: () => void;
  onWithdraw: () => void;
  onRetry: () => void;
  onOpenPlan: () => void;
  /** "Yes, write the briefing" on a flagged version (spec VAL-22): confirms it and
   * starts the draft briefing (Phase 2). */
  onConfirm?: () => void;
  /** "It's the right plan" on a rejected version, with a reason (VAL-23); it also
   * starts the draft briefing. */
  onOverride?: (reason: string) => void;
  /** "Delete this upload" / "Wrong file, remove it": a failed, rejected or flagged
   * (not yet OK'd) version only (VAL-26, VAL-22). */
  onDiscard?: () => void;
  /** "See the checks" (2026-11-02): only the results, closed with the ✕ — no
   * banner repeating the problem and no actions. */
  checksOnly?: boolean;
}

// "Acme's", but "Wrongco Holdings'" — names ending in s take only the apostrophe.
const possessive = (name: string) =>
  /s$/i.test(name) ? `${name}'` : `${name}'s`;

const checksWith = (file: PlanFile | null, status: PlanCheck["status"]) =>
  (file?.validation?.checks || []).filter(
    c => c.status === status && (status !== "warn" || c.id !== "C2")
  );

// ExecBridge buttons (Components/Button): rounded, 0.6rem × 1rem, medium weight.
const BTN =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:cursor-not-allowed disabled:opacity-50";
const PRIMARY = `${BTN} bg-[#974ae4] text-white hover:bg-[#8538d4]`;
const SECONDARY = `${BTN} border border-gray-200 bg-white text-gray-700 hover:bg-gray-50`;
const DANGER = `${BTN} bg-[#ff4f52] text-white hover:bg-[#e8393c]`;
const DANGER_QUIET = `${BTN} -ml-2 px-2 text-gray-500 hover:bg-rose-50 hover:text-rose-700`;
const GHOST = `${BTN} px-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700`;

/**
 * After choosing a file (spec QUE-03..05, NTF-05): where the plan is in line, roughly
 * when it will be ready, the reading steps, and a way out. Closing never stops the
 * work — the plan keeps its place and the person is told when it lands. Ends on
 * "ready to confirm": the draft briefing is written only once the AE confirms the
 * plan (Confirm plan, Phase 2).
 */
const UploadStatusDialog: React.FC<Props> = ({
  open,
  inline = false,
  file,
  filename,
  canEdit,
  busy = false,
  error,
  onClose,
  onWithdraw,
  onRetry,
  onOpenPlan,
  onConfirm,
  onOverride,
  onDiscard,
  checksOnly = false,
}) => {
  const [overriding, setOverriding] = useState(false);
  // Deleting an upload that was stopped or couldn't be read asks once more first.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmQueuedDelete, setConfirmQueuedDelete] = useState(false);
  const [reason, setReason] = useState("");
  const read = file?.queue?.read;
  const processing = !file || file.status === "processing";
  const queued = processing && read?.state === "queued";
  const ready = file?.status === "ready" || file?.status === "partial";
  const failed = file?.status === "failed";
  const rejected = file?.status === "rejected";
  const flagged = file?.status === "needs_review";
  const fails = checksWith(file, "fail");
  const warns = checksWith(file, "warn");
  const passedChecks = (file?.validation?.checks || []).filter(
    c => c.status === "pass"
  );
  const passed = passedChecks.length;
  // Phase 3: the server says when trying again can't help (scanned, password,
  // damaged, no pages) or the stored file is gone; absent reads as retryable.
  const canRetry = file?.retryable !== false && !file?.purged;
  const retryable = canRetry && rejected && fails.some(c => c.id === "C1");
  const overridable =
    rejected && fails.length > 0 && fails.every(c => c.overridable);
  const leave = suggestLeaving(file?.queue);
  // The customer a flagged deck says it is for, when it says so (A2); never guessed.
  const appearsFor = flagged
    ? warns.find(c => c.id === "A2" && c.appears_for)?.appears_for
    : undefined;

  // An upload is never numbered before it is confirmed (D1).
  const title = rejected
    ? "Your upload wasn't used"
    : flagged
      ? "Plan check results"
      : failed
        ? "Couldn't read your upload"
        : ready
          ? "Your upload is ready to confirm"
          : queued
            ? FLOW.queuedForParsing
            : FLOW.parsing; // D40

  let big = "Starting…";
  let sub = "until your plan is ready to view";
  let tone = "border-gray-200 bg-white";
  if (rejected) {
    big = fails[0]?.detail || file?.error || "A plan check failed.";
    sub = retryable
      ? "Try again; it usually works the second time."
      : "The current plan hasn't changed and nothing was sent to the sponsor.";
    tone = "border-rose-200 bg-rose-50";
  } else if (flagged && appearsFor) {
    big = `This looks like ${possessive(appearsFor)} plan`;
    sub = onDiscard
      ? "If it's the wrong file, remove it and upload the right one. The briefing waits for your OK."
      : "If it's the wrong file, upload the right one. The briefing waits for your OK.";
    tone = "border-amber-200 bg-amber-50";
  } else if (flagged) {
    big = `${warns.length} thing${warns.length === 1 ? "" : "s"} to check before the briefing`;
    sub = "You can open the plan now. The briefing waits for your OK.";
    tone = "border-amber-200 bg-amber-50";
  } else if (failed) {
    big = file?.error || "The file couldn't be read.";
    sub = canRetry
      ? "Try again, or upload a different file."
      : "Trying again won't help: upload a different file.";
    tone = "border-rose-200 bg-rose-50";
  } else if (ready) {
    big =
      file?.status === "partial"
        ? "Read, with gaps"
        : "Read and waiting for you";
    sub =
      file?.status === "partial"
        ? `${file?.error || "Some parts couldn't be produced."} Check what was read, then confirm it.`
        : "Check it's the right plan, then confirm it. The briefing is written only after you confirm.";
    tone = "border-emerald-200 bg-emerald-50";
  } else if (read?.stalled) {
    big = "Taking longer than usual";
    sub =
      "Izba has been told and is looking at it. There is nothing for you to do.";
    tone = "border-gray-200 bg-gray-50";
  } else if (read?.slow) {
    big = "Taking longer than usual";
    sub = "Large decks sometimes do. We'll let you know when it's done.";
    tone = "border-amber-200 bg-amber-50";
  } else if (read) {
    big = roughRange(read.done_in).replace(/^about /, "~");
  }

  const Title = inline ? "h3" : Dialog.Title;
  const body = (
    <>
      <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-4">
        <div className="min-w-0">
          <Title className="text-base font-semibold text-gray-900">
            {title}
          </Title>
          <p className="truncate text-xs text-gray-500">
            {file?.filename || filename}
            {file?.page_count ? ` · ${file.page_count} pages` : ""}
          </p>
        </div>
        {!inline && (
          <Dialog.Close asChild>
            <button type="button" aria-label="Close" className={GHOST}>
              <X className="h-4 w-4" aria-hidden />
            </button>
          </Dialog.Close>
        )}
      </div>

      <div className="px-5 pb-4">
        {!checksOnly && (
          <div
            className={`mb-3 flex items-center gap-3 rounded-lg border px-3 py-2.5 ${tone}`}
            data-testid="upload-status-eta"
          >
            {failed || rejected || flagged ? (
              <AlertTriangle
                className="h-5 w-5 shrink-0 text-rose-600"
                aria-hidden
              />
            ) : ready ? (
              <CheckCircle2
                className="h-5 w-5 shrink-0 text-emerald-600"
                aria-hidden
              />
            ) : (
              <Clock className="h-5 w-5 shrink-0 text-[#974ae4]" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <div
                className={`${failed || ready || rejected || flagged || read?.slow ? "text-sm" : "text-xl"} font-semibold leading-tight text-gray-900`}
              >
                {big}
              </div>
              {inline ? (
                <div className="text-xs text-gray-600">{sub}</div>
              ) : (
                <Dialog.Description asChild>
                  <div className="text-xs text-gray-600">{sub}</div>
                </Dialog.Description>
              )}
            </div>
            {queued && read && (
              <div
                className="shrink-0 text-right text-xs text-gray-600"
                data-testid="upload-status-ahead"
              >
                {aheadText(read.ahead)}
                {read.busy && (
                  <div className="text-amber-700">Busier than usual</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stays once read, so the AE can see every check it passed. */}
        {file && (processing || ready) && (
          <UploadProgress file={file} compact />
        )}

        {(rejected || flagged) && (
          <ul
            className="space-y-1.5 rounded-lg border border-gray-100 bg-white/70 p-3 text-sm"
            data-testid="upload-status-checks"
            aria-label="Plan checks"
          >
            {[...fails, ...warns].map(c => (
              <li key={c.id} className="flex items-start gap-2">
                <AlertTriangle
                  className={`mt-0.5 h-4 w-4 shrink-0 ${c.status === "fail" ? "text-rose-600" : "text-amber-600"}`}
                  aria-hidden
                />
                <span>
                  <span className="font-medium text-gray-900">
                    {c.problem || c.title}
                  </span>
                  <span className="block text-xs text-gray-600">
                    {c.detail}
                  </span>
                </span>
              </li>
            ))}
            {passed > 0 && (
              <li className="text-gray-600">
                {/* Every check that passed, by name, on request (2026-11-02). */}
                <details data-testid="passed-checks" className="group">
                  <summary className="flex cursor-pointer list-none items-center gap-2">
                    <CheckCircle2
                      className="h-4 w-4 shrink-0 text-emerald-600"
                      aria-hidden
                    />
                    <span>
                      {passed} other check{passed === 1 ? "" : "s"} passed
                    </span>
                    <span className="text-xs text-[#974ae4] group-open:hidden">
                      Show
                    </span>
                    <span className="hidden text-xs text-[#974ae4] group-open:inline">
                      Hide
                    </span>
                  </summary>
                  {/* The same four groups as Confirm plan (Sushant, 2026-11-04). */}
                  <ul
                    className="mt-1.5 space-y-1 pl-6 text-xs"
                    data-testid="passed-check-groups"
                  >
                    {groupChecks(passedChecks).map(g => (
                      <li key={g.key}>
                        <details>
                          <summary className="flex cursor-pointer list-none items-center gap-1.5">
                            <CheckCircle2
                              className="h-3 w-3 shrink-0 text-emerald-600"
                              aria-hidden
                            />
                            <span className="text-gray-800">{g.label}</span>
                            <span className="text-gray-400">
                              · {g.checks.length}
                            </span>
                          </summary>
                          <ul className="ml-[18px] mt-1 space-y-0.5 text-gray-500">
                            {g.checks.map(c => (
                              <li key={c.id}>{passLabel(c)}</li>
                            ))}
                          </ul>
                        </details>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            )}
          </ul>
        )}
        {overriding && (
          <div className="mt-3">
            <label
              htmlFor="override-reason"
              className="text-xs font-medium text-gray-700"
            >
              Why is this the right plan? (kept with the version; the draft
              briefing is written next)
            </label>
            <input
              id="override-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Globex is the parent company"
              className="mt-1 w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
            />
          </div>
        )}
        {processing && (
          <p className="mt-3 text-xs text-gray-500">
            {inline
              ? "You can leave this page. Your plan keeps its place in line, and we'll let you know when it's ready."
              : "You can close this. Your plan keeps its place in line, and we'll let you know when it's ready. Or keep this open — it updates by itself."}
          </p>
        )}
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>

      {!checksOnly && (
        <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-xl border-t border-gray-100 bg-gray-50 px-5 py-3">
          {processing && (
            <>
              {/* Delete while it waits in line (was "Withdraw upload"; Sushant,
                  2026-11-04): the page's one Delete button and confirm. */}
              {queued &&
                canEdit &&
                (confirmQueuedDelete ? (
                  <DeleteConfirm
                    ask={FLOW.deleteQueuedAsk}
                    busy={busy}
                    onDelete={onWithdraw}
                    onCancel={() => setConfirmQueuedDelete(false)}
                  />
                ) : (
                  <span className="mr-auto">
                    <DeleteButton
                      disabled={busy}
                      onClick={() => setConfirmQueuedDelete(true)}
                    />
                  </span>
                ))}
              {!inline && (
                <button
                  type="button"
                  className={leave ? PRIMARY : SECONDARY}
                  onClick={onClose}
                >
                  <BellRing className="h-4 w-4" aria-hidden />
                  Close, notify me
                </button>
              )}
            </>
          )}
          {ready && (
            <>
              {!inline && (
                <button type="button" className={SECONDARY} onClick={onClose}>
                  Close
                </button>
              )}
              <button type="button" className={PRIMARY} onClick={onOpenPlan}>
                {FLOW.goToConfirm}
              </button>
            </>
          )}
          {(rejected || failed) && !overriding && confirmDelete && (
            <>
              <p className="mr-auto self-center text-sm text-gray-700">
                Delete this upload and its stored file?
              </p>
              <button
                type="button"
                className={SECONDARY}
                onClick={() => setConfirmDelete(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={DANGER}
                onClick={onDiscard}
                disabled={busy}
              >
                Delete
              </button>
            </>
          )}
          {(rejected || failed) && !overriding && !confirmDelete && (
            <>
              <span className="mr-auto">
                {canEdit && onDiscard && (
                  <button
                    type="button"
                    className={DANGER_QUIET}
                    onClick={() => setConfirmDelete(true)}
                    disabled={busy}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    Delete upload
                  </button>
                )}
              </span>
              {!canEdit && !inline && (
                <button type="button" className={SECONDARY} onClick={onClose}>
                  Close
                </button>
              )}
              {canEdit && rejected && overridable && onOverride && (
                <button
                  type="button"
                  className={SECONDARY}
                  onClick={() => setOverriding(true)}
                >
                  It&apos;s the right plan
                </button>
              )}
              {canEdit &&
                ((failed && canRetry) || retryable ? (
                  <button
                    type="button"
                    className={PRIMARY}
                    onClick={onRetry}
                    disabled={busy}
                  >
                    Try again
                  </button>
                ) : (
                  <button type="button" className={PRIMARY} onClick={onClose}>
                    Upload the right file
                  </button>
                ))}
            </>
          )}
          {rejected && overriding && (
            <>
              <button
                type="button"
                className={SECONDARY}
                onClick={() => setOverriding(false)}
              >
                Back
              </button>
              <button
                type="button"
                className={PRIMARY}
                disabled={busy || reason.trim().length < 3}
                onClick={() => onOverride && onOverride(reason.trim())}
              >
                Use it and write the briefing
              </button>
            </>
          )}
          {flagged && confirmDelete && (
            <>
              <p className="mr-auto self-center text-sm text-gray-700">
                Remove this upload and its stored file?
              </p>
              <button
                type="button"
                className={SECONDARY}
                onClick={() => setConfirmDelete(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={DANGER}
                onClick={onDiscard}
                disabled={busy}
              >
                Remove
              </button>
            </>
          )}
          {flagged && !confirmDelete && (
            <>
              {/* A flagged version isn't kept until the AE OKs it, so a wrong file can
                    go for good (Sushant, 2026-10-28; VAL-22, VAL-26). */}
              <span className="mr-auto">
                {canEdit && onDiscard && (
                  <button
                    type="button"
                    className={DANGER_QUIET}
                    onClick={() => setConfirmDelete(true)}
                    disabled={busy}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    Wrong file, remove it
                  </button>
                )}
              </span>
              {canEdit && onConfirm && (
                <button
                  type="button"
                  className={SECONDARY}
                  onClick={onConfirm}
                  disabled={busy}
                >
                  {FLOW.writeBriefing}
                </button>
              )}
              <button type="button" className={PRIMARY} onClick={onOpenPlan}>
                Open plan
              </button>
            </>
          )}
        </div>
      )}
    </>
  );

  if (inline)
    return (
      <section
        data-testid="upload-status-card"
        aria-label={title}
        className="mb-4 rounded-xl border border-purple-200 bg-white shadow-sm"
      >
        {body}
      </section>
    );
  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/30" />
        <Dialog.Content
          data-testid="upload-status-dialog"
          className={`fixed left-1/2 top-1/2 z-50 w-[calc(100%-32px)] ${flagged ? "max-w-xl" : "max-w-lg"} -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white shadow-xl focus:outline-none`}
        >
          {body}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UploadStatusDialog;
